// backend/seed/seedChapters.js and other imports remain unchanged
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext, useRef, createRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import ContentBlock from '../components/ContentBlock';
import './Chapter.css';

// Accept setCurrentVisibleBlockText as a prop
function Chapter({ setCurrentVisibleBlockText }) {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ------------------------------------------------------------------ */
  /* ⬇️  STATE & REFS                                                  */
  /* ------------------------------------------------------------------ */
  const { isAuthenticated, token } = useContext(AuthContext);
  const [chapter, setChapter] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentVisibleIndex, setCurrentVisibleIndex] = useState(0);
  const blockRefs = useRef([]);
  const containerRef = useRef(null);
  const trackRef     = useRef(null);
  const thumbRef     = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  /* ------------------------------------------------------------------ */
  /* ⬇️  AUTH REDIRECT                                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isAuthenticated === false) navigate('/login');
  }, [isAuthenticated, navigate]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  FETCH CHAPTER + USER PROGRESS                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isAuthenticated || !id) return;
    setIsLoading(true);

    (async () => {
      try {
        const [chapterRes, progressRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/chapters/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!chapterRes.ok || !progressRes.ok) throw new Error('Network');

        const chapterData  = await chapterRes.json();
        const progressData = await progressRes.json();

        setChapter(chapterData);
        setUserAnswers(progressData.answers || {});
        if (chapterData && chapterData.contentBlocks) {
          blockRefs.current = Array(chapterData.contentBlocks.length + 1) // +1 for the last slide
            .fill()
            .map((_, i) => blockRefs.current[i] || createRef());
        }
      } catch (err) {
        console.error(err);
        setChapter(null);
      } finally {
        setIsLoading(false);
      }
    })();
    
    // Cleanup: Clear the block text when chapter ID changes or component unmounts
    return () => {
      if (setCurrentVisibleBlockText) {
        setCurrentVisibleBlockText('');
      }
    };
  }, [id, isAuthenticated, token, setCurrentVisibleBlockText]); // Added setCurrentVisibleBlockText

  /* ------------------------------------------------------------------ */
  /* ⬇️  PREVENT BODY SCROLLING                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = original);
  }, []);

  /* ------------------------------------------------------------------ */
  /* ⬇️  KEY / WHEEL BLOCK INSIDE CONTAINER                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    const stopWheel = (e) => e.preventDefault();
    const stopKeys  = (e) => {
      const bad = ['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '];
      const tag = e.target.tagName.toLowerCase();
      if (bad.includes(e.key) && tag !== 'input' && tag !== 'textarea' && !e.target.isContentEditable) e.preventDefault();
    };

    c.addEventListener('wheel', stopWheel, { passive:false });
    c.addEventListener('keydown', stopKeys);
    c.setAttribute('tabindex','-1');

    return () => {
      c.removeEventListener('wheel', stopWheel);
      c.removeEventListener('keydown', stopKeys);
      c.removeAttribute('tabindex');
    };
  }, [id]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  DISCRETE SCROLLBAR DRAG LOGIC                                 */
  /* ------------------------------------------------------------------ */
  const totalBlocks = chapter?.contentBlocks?.length ?? 0; // Number of actual content blocks
  const totalSlides = totalBlocks + 1; // Total slides including the "lastSlide"

  const scrollToSlide = (idx) => {
    blockRefs.current[idx]?.current?.scrollIntoView({ behavior:'auto', block:'start' });
  };

  /** convert pointer position -> slide index */
  const positionToIndex = (clientY) => {
    const track = trackRef.current;
    if (!track) return 0;
    const { top, height } = track.getBoundingClientRect();
    const y   = Math.min(Math.max(clientY - top, 0), height);
    const pct = y / height;
    return Math.round(pct * (totalSlides - 1));
  };

  const handleTrackClick = (e) => scrollToSlide(positionToIndex(e.clientY));

  const handlePointerDown = (e) => {
    setIsDragging(true);
    scrollToSlide(positionToIndex(e.clientY));
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => scrollToSlide(positionToIndex(e.clientY));
    const up   = () => setIsDragging(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [isDragging]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  ANSWER HANDLER & VISIBILITY CALLBACK                          */
  /* ------------------------------------------------------------------ */
  const updateAnswer = (blockId, ans) => setUserAnswers((p) => ({ ...p, [blockId]: ans }));
  
  const handleInView = (idx) => {
    setCurrentVisibleIndex(idx);

    if (!setCurrentVisibleBlockText) return;

    if (chapter && chapter.contentBlocks && idx < totalBlocks) { // idx is within actual contentBlocks
      const currentBlock = chapter.contentBlocks[idx];
      let blockText = '';
      if (currentBlock.type === 'text') {
        blockText = currentBlock.data || "";
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = blockText;
        blockText = (tempDiv.textContent || tempDiv.innerText || "").trim();
      } else if (currentBlock.type === 'question' && currentBlock.data && currentBlock.data.prompt) {
        blockText = currentBlock.data.prompt.trim();
      } else if (currentBlock.type === 'table' && currentBlock.data) {
        if (Array.isArray(currentBlock.data.headings) && currentBlock.data.headings.length > 0) {
          blockText = `User is viewing a table with columns: ${currentBlock.data.headings.join(', ')}.`;
        } else {
          blockText = "User is viewing a data table.";
        }
      } else if (currentBlock.type === 'image' && currentBlock.data) {
        blockText = `User is viewing an image.`;
      } else if (currentBlock.type === 'ai' && currentBlock.data && currentBlock.data.welcomeMessage) {
        blockText = `User is interacting with an AI chat block. Welcome: "${currentBlock.data.welcomeMessage}"`;
      }
      setCurrentVisibleBlockText(blockText);
    } else if (idx === totalBlocks) { // This is the "lastSlide"
        setCurrentVisibleBlockText("User is viewing the final summary slide of the chapter.");
    } else {
      setCurrentVisibleBlockText(''); 
    }
  };

  /* ------------------------------------------------------------------ */
  /* ⬇️  RENDER STATES                                                */
  /* ------------------------------------------------------------------ */
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-white">
        <div className="w-80 mb-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-black rounded-full transition-all duration-1000 ease-out"
              style={{
                width: '85%',
                animation: 'loadingProgress 0.25s ease-out forwards'
              }}
            />
          </div>
        </div>
        <style jsx>{`
          @keyframes loadingProgress {
            0% { width: 0% }
            50% { width: 60% }
            80% { width: 85% }
            100% { width: 95% }
          }
        `}</style>
      </div>
    );
  }
  if (!chapter)                               return <div className="flex flex-col items-center h-screen justify-center">Error loading.</div>;

  // Calculate unanswered question indexes for the last slide
  const unansweredIndexes = [];
  if (chapter && chapter.contentBlocks) {
    chapter.contentBlocks.forEach((block, index) => {
      if (block.type === 'question') {
        const answer = userAnswers[block._id];
        // Consider a question unanswered if there's no answer, or if the answer is an empty string (common for text inputs)
        // You might need to adjust this condition based on how your QuestionComponent stores empty/default answers for various question types
        if (answer === undefined || answer === null || (typeof answer === 'string' && answer.trim() === '') || (Array.isArray(answer) && answer.length === 0)) {
          unansweredIndexes.push(index + 1); // Store 1-based index for display
        }
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* ⬇️  UI                                                           */
  /* ------------------------------------------------------------------ */
  return (
    <div ref={containerRef} className="chapter-container h-screen w-[2/3] overflow-hidden relative bg-white">
      {chapter && chapter.contentBlocks && chapter.contentBlocks.map((block, i) => (
        <ContentBlock
          key={block._id || i}
          blockRef={blockRefs.current[i]}
          block={block}
          index={i}
          chapterId={chapter._id}
          userAnswers={userAnswers}
          totalBlocks={totalSlides}
          updateAnswer={updateAnswer}
          onInView={handleInView}
        />
      ))}
      {/* last slide */}
      {chapter && (
          <ContentBlock
            key="last"
            blockRef={blockRefs.current[totalBlocks]} // Ref for the last slide
            block={{
              type: 'lastSlide',
              adjustedUnansweredIndexes: unansweredIndexes 
            }}
            index={totalBlocks} // Index for the last slide
            chapterId={chapter._id}
            userAnswers={userAnswers}
            totalBlocks={totalSlides}
            onInView={handleInView} // Ensure this is called for the last slide too
          />
      )}

        <div className="w-2/3 fixed bottom-4 z-20 flex justify-center gap-4">
          <button onClick={()=>scrollToSlide(Math.max(0, currentVisibleIndex-1))} disabled={currentVisibleIndex===0} className="px-4 py-2 bg-black text-white rounded disabled:opacity-40">← Back</button>
          <button onClick={()=>scrollToSlide(Math.min(totalSlides-1, currentVisibleIndex+1))} disabled={currentVisibleIndex===totalSlides-1} className="px-4 py-2 bg-black text-white rounded disabled:opacity-40">Next →</button>
        </div>

        {/* slide counter */}
      <div className="fixed font-body left-4 top-[calc(50%+12.5rem)] text-gray-600">{currentVisibleIndex+1}/{totalSlides}</div>

      {/* discrete scrollbar */}
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className="fixed left-[28px] top-1/2 -translate-y-1/2 h-96 w-2 bg-gray-300 cursor-pointer z-[90]"
      >
        {/* Filled portion of the scrollbar */}
        <div
          className="absolute top-0 left-0 w-full bg-black"
          style={{
            height: totalSlides > 1 ? `${(currentVisibleIndex / (totalSlides - 1)) * 100}%` : '0%',
          }}
        />
        <div
          ref={thumbRef}
          onPointerDown={handlePointerDown}
          className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-black touch-none"
          style={{ top: totalSlides > 1 ? `${(currentVisibleIndex / (totalSlides - 1)) * 100}%` : '0%' }}
        />
      </div>
    </div>
  );
}

export default Chapter;
